/**
 * WalletTxContextBase - Shared context and types for wallet transaction queue
 *
 * CRITICAL: This file must NOT import wagmi, rainbowkit, or any wallet SDK.
 * It defines only the context shape, types, and hook that are shared between
 * the real provider (WalletTxContext.tsx) and the stub provider (WalletTxContextStub.tsx).
 *
 * This architecture ensures both providers use the same React context instance,
 * preventing "useWalletTxQueue must be used within a WalletTxProvider" errors.
 */

import { createContext, useContext } from 'react';

// ============================================================================
// Types
// ============================================================================

export type TxJobType = 'APPROVE' | 'MINT' | 'REACTIVATE' | 'POOL' | 'FAUCET' | 'REVOKE' | 'OTHER';

export type TxJobStatus = 'queued' | 'signing' | 'pending' | 'confirming' | 'done' | 'failed';

export type TxErrorCategory = 'user_rejected' | 'rpc_internal' | 'wallet_provider_internal' | 'insufficient_funds' | 'contract_revert' | 'timeout' | 'unknown';

// Single-flight gating: reason why wallet is busy
export type WalletBusyReason = 'idle' | 'tx_in_flight' | 'awaiting_confirmation' | 'nonce_converging';

/** Metadata for tracking which block a tx is for */
export interface TxJobMetadata {
  dbBlockId?: string;
  onChainBlockId?: string;
  action?: 'POOL' | 'REVOKE' | 'ADD' | 'APPROVE';
}

export interface TxJob {
  jobId: string;
  type: TxJobType;
  label: string; // Human-readable description (e.g., "Reactivate Block #26")
  execute: () => Promise<`0x${string}` | null>; // Returns tx hash or null if rejected
  onSuccess?: (txHash: string) => void;
  onError?: (error: Error, category: TxErrorCategory) => void;
  enqueuedAt: number;
  /**
   * Set to true after preflight simulation (eth_call) passes.
   * Used to classify -32603 as wallet_provider_internal (sim passed but send failed)
   */
  preflightSimOk?: boolean;
  /** Metadata for per-block pending state tracking */
  metadata?: TxJobMetadata;
}

export interface ActiveTx {
  jobId: string;
  type: TxJobType;
  label: string;
  status: TxJobStatus;
  txHash?: string;
  error?: string;
  errorCategory?: TxErrorCategory;
  metadata?: TxJobMetadata;
}

export interface WalletTxQueueState {
  queueLength: number;
  currentJob: ActiveTx | null;
  queuedJobs: Array<{ jobId: string; type: TxJobType; label: string }>;
  isProcessing: boolean;
}

export interface WalletTxContextValue {
  // Queue state (for UI)
  queueState: WalletTxQueueState;

  // Single-flight gating (D3 invariant)
  // Use these to disable all write buttons when a tx is processing
  isTxBusy: boolean;
  walletBusyReason: WalletBusyReason;
  walletBusyJobId: string | null;

  // Main API: Add a transaction to the queue
  enqueueTx: (job: Omit<TxJob, 'jobId' | 'enqueuedAt'>) => string; // Returns jobId

  // Check if a specific job is in queue or processing
  isJobQueued: (jobId: string) => boolean;
  isJobProcessing: (jobId: string) => boolean;

  // Check if a block has a pending tx (for per-row UI state)
  getPendingJobForBlock: (dbBlockId: string) => ActiveTx | null;

  // Get writeContractAsync for direct use (advanced)
  // Using 'any' to avoid wagmi type imports
  writeContractAsync: (...args: any[]) => Promise<any>;

  // Public client for waiting on receipts
  // Using 'any' to avoid wagmi type imports
  publicClient: any;
}

/**
 * Parsed wallet transaction error with canonical category
 */
export interface ParsedWalletTxError {
  category: TxErrorCategory;
  code: number | string | null;
  message: string;
  originalError: any;
  /** Actionable hints for user when error is diagnosable */
  diagnosticHints?: string[];
}

// ============================================================================
// Context
// ============================================================================

export const WalletTxContext = createContext<WalletTxContextValue | null>(null);
WalletTxContext.displayName = 'WalletTxContext';

// ============================================================================
// Hook
// ============================================================================

export function useWalletTxQueue(): WalletTxContextValue {
  const context = useContext(WalletTxContext);
  if (!context) {
    throw new Error('useWalletTxQueue must be used within a WalletTxProvider');
  }
  return context;
}

// ============================================================================
// Helper Functions (no wagmi dependencies)
// ============================================================================

export function getTxStatusLabel(status: TxJobStatus): string {
  switch (status) {
    case 'queued': return 'Queued';
    case 'signing': return 'Awaiting Signature';
    case 'pending': return 'Pending';
    case 'confirming': return 'Confirming';
    case 'done': return 'Complete';
    case 'failed': return 'Failed';
    default: return status;
  }
}

export function getErrorCategoryLabel(category: TxErrorCategory): string {
  switch (category) {
    case 'user_rejected': return 'Cancelled';
    case 'rpc_internal': return 'Network Busy (retrying...)';
    case 'wallet_provider_internal': return 'Wallet Error';
    case 'insufficient_funds': return 'Insufficient Funds';
    case 'contract_revert': return 'Transaction Failed';
    case 'timeout': return 'Timeout';
    default: return 'Error';
  }
}
